/**
 * The queue with its scheduled replies joined on, plus the counts the chips render.
 *
 * Extracted from `RequestsTab` for its 500-line ceiling, and it is a coherent unit on its own: one
 * place that decides what the tab's list IS, so a chip counting one list while the accordion renders
 * another cannot happen.
 *
 * ⚠️ **The scheduled flag is a JOIN, never a field on the item.** `RequestsTab.applyResponse`
 * replaces `items` wholesale on every refetch, so a flag written onto an item would survive exactly
 * until the next inbox check and then vanish. Recomputing it from the store on each render means no
 * refetch can lose it. The proxy never sends these fields — see `InboundRequest`.
 */
import { useMemo } from 'react';
import { isScheduled, isUnread, needsAnswer, noReplyNeeded, readyToSend } from './requestGroups';
import { useScheduledSends } from './useScheduledSends';
import type { InboundRequest } from '../../types/taInboundRequests';

export interface DecoratedRequests {
  /** The queue, with `scheduledSendAt` / `scheduledSendId` set where a reply is queued. */
  decorated: InboundRequest[];
  needsHuman: number;
  noReplyCount: number;
  drafted: number;
  unreadCount: number;
  scheduledCount: number;
  /** Re-ask the server about queued replies — wired to the tab's "Check inbox". */
  reloadScheduled: () => Promise<void>;
}

export function useDecoratedRequests(items: InboundRequest[]): DecoratedRequests {
  const { pendingByMessage, reload: reloadScheduled } = useScheduledSends();

  /**
   * Keyed on the MESSAGE, not the thread: a second question on the same thread is a different card
   * and must keep its own send button, even while an answer to the earlier one is queued.
   */
  const decorated = useMemo(
    () =>
      items.map((i) => {
        const row = pendingByMessage.get(i.messageId);
        return row ? { ...i, scheduledSendAt: row.sendAt, scheduledSendId: row.id } : i;
      }),
    [items, pendingByMessage]
  );

  /**
   * One predicate per bucket, shared with the filter (`requestGroups.ts`), so a chip reading
   * "2 need you" and the list it filters to cannot disagree.
   *
   * `no_reply` is the one undrafted state that is NOT outstanding work: the thread ended in a
   * closing thanks, and counting it would nag recruiters about mail that needs nothing. A cold-lane
   * item always needs a human — "not in Lever" can mean a real candidate wrote from another
   * address, and its prepared reply would be wrong for them.
   */
  return useMemo(
    () => ({
      decorated,
      needsHuman: decorated.filter(needsAnswer).length,
      noReplyCount: decorated.filter(noReplyNeeded).length,
      drafted: decorated.filter(readyToSend).length,
      // Unread is the one count that is a FACT about the mailbox, not an inference — see sortItems.
      unreadCount: decorated.filter(isUnread).length,
      scheduledCount: decorated.filter(isScheduled).length,
      reloadScheduled,
    }),
    [decorated, reloadScheduled]
  );
}
